// @owners { team: patients }

import { parse } from 'date-fns';
import { DATE_FORMATS } from '~shared/helpers/date';
import { getURLParams } from '~shared/helpers/helper';
import { type UserCreateRequest } from '~shared/types';
import { type SignUpFormValues } from '~web/features/onboarding/components/SignupForm';

export const getOnboardingInsuranceInputTitle = ({
  hasInsurance,
  showProgyny,
}: {
  hasInsurance: boolean;
  showProgyny: boolean;
}) => {
  if (hasInsurance) {
    return 'Add another benefit';
  }

  return showProgyny
    ? 'Along with your Progyny benefits, do you have additional insurance?'
    : 'Add your prescription benefits';
};

export const getOnboardingInsuranceInputSubtitle = ({
  showProgyny,
  hasInsurance,
}: {
  hasInsurance: boolean;
  showProgyny: boolean;
}) => {
  if (showProgyny) {
    return 'This helps us find you a great price on medications not covered by Progyny.';
  } else if (hasInsurance) {
    return 'Have additional prescription benefits or savings cards? Add them so we can get you the best pricing.';
  } else {
    return 'Have prescription insurance or savings cards? Add them so we can get you the best pricing.';
  }
};

export const getSentenceCaseDosageForm = (dosageForm?: string | null) => {
  if (!dosageForm) return '';
  return dosageForm
    .split(' ')
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Prepares form data to sign up a new user account
 * @returns UserCreateRequest
 */
export const getSignUpUser = (values: SignUpFormValues) => {
  const params = getURLParams();
  const tempPassword = params.token;
  const data: UserCreateRequest = { ...values };
  delete params.token;

  if (params.utm_campaign || params.utm_source || params.utm_medium || params.utm_term || params.utm_content) {
    data.utm_params = {
      name: params.utm_campaign,
      source: params.utm_source,
      medium: params.utm_medium,
      term: params.utm_term,
      content: params.utm_content,
    };
  }

  data.current_password = tempPassword;

  if (data.date_of_birth) {
    data.date_of_birth = parse(data.date_of_birth, DATE_FORMATS.DATE_INPUT_FORMAT, 0).toISOString();
  }

  data.referral_code = data.referral_code ? data.referral_code.trim() : null;

  return data;
};
